import {
  ScreenSpaceEventHandler,
  defined,
  ScreenSpaceEventType,
  Cartesian3,
  JulianDate,
  Color,
  Cartographic,
  Math as CesiumMath,
  ColorMaterialProperty,
  PolylineDashMaterialProperty,
  CustomDataSource,
  Cartesian2,
} from "cesium";
import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import { useEffect, useRef } from "react";

function getFirstLastCartesian(entity, time) {
  const posProp = entity?.polyline?.positions;
  if (!posProp) return null;

  let arr;
  try {
    if (Array.isArray(posProp)) arr = posProp;
    else if (typeof posProp.getValue === "function") arr = posProp.getValue(time);
    else arr = posProp;
  } catch {
    return null;
  }

  if (!Array.isArray(arr) || arr.length < 2) return null;

  let first = arr[0];
  let last = arr[arr.length - 1];

  const MIN_DIST_M = 0.5;
  if (Cartesian3.distance(first, last) < MIN_DIST_M) {
    for (let i = 1; i < arr.length; i++) {
      if (Cartesian3.distance(arr[0], arr[i]) >= MIN_DIST_M) {
        last = arr[i];
        break;
      }
    }

    if (Cartesian3.distance(arr[0], last) < MIN_DIST_M) {
      for (let i = arr.length - 2; i >= 0; i--) {
        if (Cartesian3.distance(arr[arr.length - 1], arr[i]) >= MIN_DIST_M) {
          first = arr[i];
          break;
        }
      }
      if (Cartesian3.distance(first, arr[arr.length - 1]) >= MIN_DIST_M) last = arr[arr.length - 1];
    }
  }

  return first && last && first !== last ? { first, last } : null;
}

const showMapPopupForm = ({ position = { top: "100px", right: "65px" }, onSave = () => {}, onClose = () => {}, defaultValues = {}, isSameDirection = true }) => {
  // --- Avoid duplicate popups
  const existingPopup = document.getElementById("map-popup-form");
  if (existingPopup) {
    try {
      existingPopup.remove();
    } catch {
      /* ignore */
    }
  }

  // --- Root container
  const container = document.createElement("div");
  container.id = "map-popup-form";
  Object.assign(container.style, {
    position: "absolute",
    top: position.top,
    right: position.right,
    zIndex: "10000",
    backgroundColor: "white",
    padding: "16px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
    minWidth: "280px",
    fontFamily: "sans-serif",
  });

  // --- Close button
  const closeBtn = document.createElement("button");
  closeBtn.innerText = "✖";
  Object.assign(closeBtn.style, {
    position: "absolute",
    top: "6px",
    right: "8px",
    background: "none",
    border: "none",
    fontSize: "16px",
    cursor: "pointer",
  });
  closeBtn.onclick = () => {
    try {
      container.remove();
    } catch {
      /* ignore */
    }
    // cleanup space listener if set
    if (window.__mapPopup_onSpacePress) {
      window.removeEventListener("keydown", window.__mapPopup_onSpacePress);
      delete window.__mapPopup_onSpacePress;
    }

    window.removeEventListener("keydown", onKeyDown);
    onClose();
  };
  container.appendChild(closeBtn);

  // --- Close with ESC
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      try {
        container.remove();
      } catch {
        /* ignore */
      }
      if (window.__mapPopup_onSpacePress) {
        window.removeEventListener("keydown", window.__mapPopup_onSpacePress);
        delete window.__mapPopup_onSpacePress;
      }
      window.removeEventListener("keydown", onKeyDown);
      onClose();
    }
  };
  window.addEventListener("keydown", onKeyDown);

  // --- Title
  const title = document.createElement("div");
  title.innerText = "Fill Information";
  Object.assign(title.style, {
    fontWeight: "bold",
    marginBottom: "12px",
  });
  container.appendChild(title);

  // --- Read-only info rows
  const infoRow = (label, value) => {
    const row = document.createElement("div");
    Object.assign(row.style, {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      fontSize: "13px",
      padding: "6px 8px",
      background: "#f7f7f7",
      border: "1px solid #eee",
      borderRadius: "6px",
      marginBottom: "8px",
      wordBreak: "break-word",
    });

    const lbl = document.createElement("span");
    lbl.innerText = label;
    lbl.style.color = "#333";
    lbl.style.fontWeight = "600";

    const val = document.createElement("span");
    val.innerText = value ?? "—";
    val.style.color = "#555";

    row.appendChild(lbl);
    row.appendChild(val);
    return row;
  };

  const leftType = defaultValues.left_line_type ?? "";
  const rightType = defaultValues.right_line_type ?? "";
  container.appendChild(infoRow("Left Line Type:", leftType));
  container.appendChild(infoRow("Right Line Type:", rightType));

  // --- Divider
  const hr = document.createElement("div");
  Object.assign(hr.style, {
    height: "1px",
    background: "#e6e6e6",
    margin: "10px 0",
  });
  container.appendChild(hr);

  // --- Inputs registry
  const inputs = {};

  // --- Helpers to create fields
  function createDropdown(label, name, options) {
    const wrapper = document.createElement("div");
    wrapper.style.marginBottom = "10px";

    const lbl = document.createElement("label");
    lbl.innerText = label;
    Object.assign(lbl.style, {
      display: "block",
      fontSize: "13px",
      marginBottom: "4px",
    });
    lbl.setAttribute("for", `select-${name}`);

    const select = document.createElement("select");
    select.id = `select-${name}`;
    Object.assign(select.style, {
      width: "100%",
      padding: "6px",
      border: "1px solid #ccc",
      borderRadius: "4px",
    });

    options.forEach((opt) => {
      const option = document.createElement("option");
      option.value = opt;
      option.innerText = opt;
      select.appendChild(option);
    });

    // Set default or fallback to first
    select.value = defaultValues[name] || options[0];

    // Set default or fallback to first
    const raw = defaultValues[name];
    select.value = raw || options[0];

    // If the given default is not one of the options, normalize for known fields
    if (raw != null && raw !== "" && !options.includes(String(raw))) {
      if (name === "speed") {
        const m = String(raw).match(/(\d+)/);
        if (m) {
          const normalized = `${m[1]}mph`;
          if (options.includes(normalized)) {
            select.value = normalized;
          }
        }
      }
    }

    inputs[name] = select;
    wrapper.appendChild(lbl);
    wrapper.appendChild(select);
    container.appendChild(wrapper);
  }

  function createCheckboxYesNo(label, name) {
    const wrapper = document.createElement("div");
    wrapper.style.marginBottom = "10px";

    const lbl = document.createElement("label");
    Object.assign(lbl.style, {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "13px",
    });

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.id = `checkbox-${name}`;
    checkbox.style.cursor = "pointer";

    const initial = defaultValues[name];
    const isYes = initial === true || initial === "yes" || initial === "true" || initial === 1;
    checkbox.checked = !!isYes;

    const text = document.createElement("span");
    text.innerText = label;

    lbl.appendChild(checkbox);
    lbl.appendChild(text);
    lbl.setAttribute("for", `checkbox-${name}`);

    inputs[name] = checkbox;
    wrapper.appendChild(lbl);
    container.appendChild(wrapper);
  }

  // --- Dropdowns and checkbox
  createDropdown("Direction", "direction", ["straight", "left", "right", "exit_right", "exit_left", "merge_right", "merge_left", "split_right", "split_left", "u_turn_left", "u_turn_right"]);
  createDropdown("Participant", "participant", ["all-vehicles", "bicycle", "truck", "bus", "taxi", "emergency-vehicle", "motorcycle"]);
  createDropdown("Speed", "speed", ["5mph", "10mph", "15mph", "20mph", "25mph", "30mph", "35mph", "40mph", "45mph", "50mph", "55mph", "60mph", "65mph", "70mph", "75mph", "80mph", "85mph", "90mph"]);
  // Auto-set speed when direction is one of the specified values
  const DIR_SPEED_15 = new Set(["left", "right", "split_right", "split_left", "u_turn_left", "u_turn_right"]);

  const applyDirectionSpeedRule = () => {
    const dir = inputs?.direction?.value;
    if (DIR_SPEED_15.has(dir) && inputs?.speed) {
      inputs.speed.value = "15mph";
    }
  };

  if (inputs?.direction) {
    inputs.direction.addEventListener("change", applyDirectionSpeedRule);
  }

  // Apply once for initial/default values too
  applyDirectionSpeedRule();

  createDropdown("Surface", "surface", ["asphalt", "concrete", "gravel"]);
  createCheckboxYesNo("One way", "one_way");
  createDropdown("Subtype", "subtype", ["road", "shoulder"]);
  createDropdown("Function", "function", ["", "turn", "suicide lane", "exit", "u-turn", "deadend", "underpass"]);
  createCheckboxYesNo("Only consider right lane direction", "only_right_dir");
  createCheckboxYesNo("Auto save", "auto_save"); // yes/no

  // --- Warning if directions differ
  if (!isSameDirection) {
    const warn = document.createElement("div");
    warn.innerText = "⚠️ Warning: RIGHT and LEFT have different direction!";
    Object.assign(warn.style, {
      color: "orange",
      fontSize: "13px",
      fontWeight: "bold",
      margin: "8px 0",
    });
    container.appendChild(warn);
  }

  // --- Save button
  const saveBtn = document.createElement("button");
  saveBtn.innerText = "Save";
  Object.assign(saveBtn.style, {
    marginTop: "8px",
    padding: "6px 12px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  });

  saveBtn.onclick = () => {
    const data = {};
    for (const name in inputs) {
      const el = inputs[name];
      if (el instanceof HTMLInputElement && el.type === "checkbox") {
        data[name] = el.checked ? "yes" : "no";
      } else if (el) {
        data[name] = el.value;
      }
    }
    try {
      container.remove();
    } catch {
      /* ignore */
    }
    window.removeEventListener("keydown", onKeyDown);
    onSave(data);
  };

  // trigger Save when Space pressed, ignore typing in inputs
  const onSpacePress = (e) => {
    if (e.code !== "Space" && e.key !== " ") return; // only Space
    const t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    e.preventDefault();
    saveBtn.click(); // trigger existing save handler
  };

  // store globally for safe removal from other handlers
  window.__mapPopup_onSpacePress = onSpacePress;
  window.addEventListener("keydown", onSpacePress);

  // cleanup helper
  const _cleanupMapPopupSpace = () => {
    if (window.__mapPopup_onSpacePress) {
      window.removeEventListener("keydown", window.__mapPopup_onSpacePress);
      delete window.__mapPopup_onSpacePress;
    }
  };

  // ensure cleanup when user saves via UI
  saveBtn.addEventListener("click", _cleanupMapPopupSpace);

  container.appendChild(saveBtn);
  document.body.appendChild(container);
};

const detectLayerType = (rawName) => {
  // Guard: ensure input is a string-like value
  if (rawName == null) return null;

  // Normalize label using helper (safe to handle non-string input)
  const n = normalizeLabel(rawName);

  // Match against known keywords
  if (n.includes("virtualline")) return "virtual";
  if (n.includes("lanemarker")) return "marker";
  if (n.includes("turningline")) return "turning";

  // No match found
  return null;
};

const selectTwoPolylines = (viewer, cancelRef = {}) => {
  return new Promise((resolve, reject) => {
    const DIR_TOL_DEG = 5;

    const picked = [];
    // Input handler for mouse clicks
    const handler = new ScreenSpaceEventHandler(viewer?.scene?.canvas);

    // --- Cleanup helpers ---
    const detach = () => {
      try {
        handler?.destroy();
      } catch {
        /* ignore */
      }
      window.removeEventListener("keydown", onKeydown);
    };

    const restoreHighlights = () => {
      try {
        for (const p of picked) {
          if (p?.restore) p.restore();
        }
      } catch {
        /* ignore */
      }
    };

    // Expose cancel/restore externally
    cancelRef.restoreHighlights = restoreHighlights;
    cancelRef.cancel = () => {
      detach();
      restoreHighlights();
      reject(new Error("Selection canceled"));
    };

    function removeLastPicked() {
      // remove last pick highlight only
      try {
        const last = picked.pop();
        if (last?.restore) last.restore();
      } catch {}
    }

    const onKeydown = (e) => {
      if (e.key === "Escape") {
        // If user already picked one item, deselect that one and continue picking.
        if (picked.length > 0) {
          removeLastPicked(); // un-highlight last pick and allow re-pick
          return; // do not cancel the whole selection promise
        }

        // If nothing picked yet, treat Escape as cancel.
        detach();
        restoreHighlights();
        reject(new Error("Selection canceled (Esc)"));
      }
    };
    window.addEventListener("keydown", onKeydown);

    // ---------------- Helpers for direction on a sphere ----------------

    /**
     * Compute forward azimuth (bearing) from p1 to p2 in radians.
     * Uses cartographic lon/lat and great-circle formula. Output in [0, 2π).
     */
    function bearingRadians(p1, p2) {
      const c1 = Cartographic.fromCartesian(p1);
      const c2 = Cartographic.fromCartesian(p2);
      const dLon = c2.longitude - c1.longitude;

      const y = Math.sin(dLon) * Math.cos(c2.latitude);
      const x = Math.cos(c1.latitude) * Math.sin(c2.latitude) - Math.sin(c1.latitude) * Math.cos(c2.latitude) * Math.cos(dLon);

      let brng = Math.atan2(y, x); // -π..π
      if (brng < 0) brng += CesiumMath.TWO_PI; // 0..2π
      return brng;
    }

    /**
     * Smallest absolute difference between two bearings (0..π).
     */
    function smallestBearingDiff(rad1, rad2) {
      let diff = Math.abs(rad2 - rad1) % CesiumMath.TWO_PI; // 0..2π
      if (diff > Math.PI) diff = CesiumMath.TWO_PI - diff; // 0..π
      return diff;
    }

    // ---------------- Mouse click handler ----------------
    handler?.setInputAction((movement) => {
      const pickedObj = viewer?.scene?.pick(movement?.position);
      if (!defined(pickedObj)) return;

      const entity = pickedObj.id || pickedObj.primitive?.id;
      if (!entity?.polyline?.positions) return;

      const dataSource = entity?.entityCollection?.owner || null;
      const layerName = dataSource?.layerName ?? dataSource?.name ?? "";
      const layerType = detectLayerType(layerName);
      if (!layerType) return;

      // Ignore duplicate click on same entity
      if (picked.length === 1 && picked[0].entity === entity) return;

      let restore = null;
      if (picked.length === 0) {
        restore = highlightPolylineEntity(viewer, entity, Color.BLUE, 6);
      } else if (picked.length === 1) {
        restore = highlightPolylineEntity(viewer, entity, Color.PURPLE, 6);
      }

      picked.push({ entity, layerType, restore });

      // Once two are picked, resolve and cleanup
      if (picked.length === 2) {
        detach();

        const [first, second] = picked;
        const time = viewer?.clock?.currentTime;

        // --- Robust sameDirection via bearing comparison ---
        let sameDirection = false;
        const seg1 = getFirstLastCartesian(first.entity, time);
        const seg2 = getFirstLastCartesian(second.entity, time);

        if (seg1 && seg2) {
          const b1 = bearingRadians(seg1.first, seg1.last);
          const b2 = bearingRadians(seg2.first, seg2.last);

          const diff = smallestBearingDiff(b1, b2); // 0..π

          sameDirection = diff <= CesiumMath.toRadians(DIR_TOL_DEG);
        }

        resolve([first, second, sameDirection]);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);

    // Allow external teardown
    cancelRef._teardown = () => {
      detach();
      restoreHighlights();
    };
  });
};

const createLineLinkInfoFromEntities = (rightEntity, leftEntity, searchKey = "id") => {
  // --- Helper: safely resolve Cesium Property or plain value from an entity's properties ---
  const resolveProp = (entity, key) => {
    // If properties or the key are missing, return null
    if (!entity?.properties?.[key]) return null;

    const prop = entity.properties[key];
    try {
      // Cesium Property has getValue(JulianDate) — use "now" to resolve time-dynamic values
      return typeof prop.getValue === "function" ? prop.getValue(JulianDate.now()) : prop;
    } catch {
      return null; // If property throws on getValue, degrade gracefully
    }
  };

  // --- Helper: try to reach the DataSource that owns this entity and read its layerName ---
  const getLayerNameFromEntity = (entity) => {
    // Common Cesium pattern: entity.entityCollection.owner === the DataSource
    const ds =
      entity?.entityCollection?.owner || // typical in Cesium
      entity?.collection?.owner || // some builds use "collection"
      entity?.dataSource || // custom attachment (if your app set it)
      entity?._dataSource ||
      null; // last-resort private field (defensive)

    // If data source exists and has a layerName field, use it
    if (ds && typeof ds === "object" && "layerName" in ds && ds.layerName != null) {
      return ds.layerName;
    }

    // Fallback: sometimes layerName may be mirrored on entity.properties.layerName
    const p = entity?.properties?.layerName;
    if (p) {
      try {
        return typeof p.getValue === "function" ? p.getValue(JulianDate.now()) : p;
      } catch {
        /* ignore and fall through */
      }
    }

    return null; // No layer name found
  };

  // Resolve IDs from the requested property key (default "id")
  const rightId = resolveProp(rightEntity, searchKey);
  const leftId = resolveProp(leftEntity, searchKey);

  // Resolve layer names from each entity's owning data source (defensive)
  const rightLayerName = getLayerNameFromEntity(rightEntity);
  const leftLayerName = getLayerNameFromEntity(leftEntity);

  // Map layerName -> type using your provided detector
  const rightType = detectLayerType(rightLayerName);
  const leftType = detectLayerType(leftLayerName);

  // Return the structured result (fill the two previously-null fields)
  return {
    right_line_id: rightId ?? null, // keep null if not resolvable
    left_line_id: leftId ?? null, // keep null if not resolvable
    right_line_type: rightType ?? null, // derived from dataSource.layerName
    left_line_type: leftType ?? null, // derived from dataSource.layerName
  };
};

const createCenterLineBetweenEntities = (rightEntity, leftEntity) => {
  // --- Basic validation (both entities must have polyline positions) ---
  if (!rightEntity?.polyline?.positions || !leftEntity?.polyline?.positions) {
    console.warn("Entities must have polyline positions.");
    return null;
  }

  // --- Resolve dynamic Cesium position properties at 'now' ---
  const now = JulianDate.now(); // current time for time-dynamic properties
  const rightPositions = rightEntity.polyline.positions.getValue(now) || []; // Cartesian3[]
  const leftPositions = leftEntity.polyline.positions.getValue(now) || []; // Cartesian3[]

  // --- Need at least 2 points per line (start & end) ---
  if (rightPositions.length < 2 || leftPositions.length < 2) {
    console.warn("Each polyline must have at least 2 positions.");
    return null;
  }

  // --- Helpers ---
  /**
   * getMiddleCartographic
   * Computes midpoint in Cartographic space between two Cartesian3 positions.
   */
  const getMiddleCartographic = (p1, p2) => {
    if (!p1 || !p2) return null; // guard nulls
    const c1 = Cartographic.fromCartesian(p1); // lon/lat/height (radians, meters)
    const c2 = Cartographic.fromCartesian(p2);
    const lat = (c1.latitude + c2.latitude) / 2; // average latitude (rad)
    const lon = (c1.longitude + c2.longitude) / 2; // average longitude (rad)
    const height = (c1.height + c2.height) / 2; // average height (m)

    return new Cartographic(lon, lat, height); // return midpoint cartographic
  };

  /**
   * pickLeftUsageIndices
   * Decides whether left line should be used as-is or swapped (only endpoints),
   * using the RIGHT line as direction reference. No mutation is performed.
   * Strategy: compare sum of endpoint pairwise distances for (same) vs (cross).
   */
  const pickLeftUsageIndices = () => {
    const rFirst = rightPositions[0];
    const rLast = rightPositions[rightPositions.length - 1];
    const lFirst = leftPositions[0];
    const lLast = leftPositions[leftPositions.length - 1];

    /**
     * محاسبه فاصله دو نقطه با نادیده گرفتن ارتفاع
     */
    const getFlatDistance = (pos1, pos2) => {
      // تبدیل به مختصات جغرافیایی (طول، عرض، ارتفاع)
      const carto1 = Cartographic.fromCartesian(pos1);
      const carto2 = Cartographic.fromCartesian(pos2);

      // صفر کردن ارتفاع
      carto1.height = 0;
      carto2.height = 0;

      // تبدیل مجدد به دکارتی برای محاسبه فاصله خطی
      const flatPos1 = Cartographic.toCartesian(carto1);
      const flatPos2 = Cartographic.toCartesian(carto2);

      return Cartesian3.distance(flatPos1, flatPos2);
    };

    const sameSum = getFlatDistance(rFirst, lFirst) + getFlatDistance(rLast, lLast);
    const crossSum = getFlatDistance(rFirst, lLast) + getFlatDistance(rLast, lFirst);

    const isOpposite = crossSum < sameSum;

    return isOpposite ? { leftStartIdx: leftPositions.length - 1, leftEndIdx: 0 } : { leftStartIdx: 0, leftEndIdx: leftPositions.length - 1 };
  };

  // --- Decide how to consume left endpoints relative to right (no geometry reversal) ---
  const { leftStartIdx, leftEndIdx } = pickLeftUsageIndices();

  // --- Build start & end midpoints using direction-aware endpoint usage ---
  const startCarto = getMiddleCartographic(
    rightPositions[0], // right start (reference)
    leftPositions[leftStartIdx], // left start (potentially swapped usage)
  );
  const endCarto = getMiddleCartographic(
    rightPositions[rightPositions.length - 1], // right end (reference)
    leftPositions[leftEndIdx], // left end (potentially swapped usage)
  );

  // --- Validate midpoint computations ---
  if (!startCarto || !endCarto) {
    console.warn("Failed to compute midpoint cartographics.");
    return null;
  }

  // --- Convert radians to degrees and return compact objects (lat/lng/height) ---
  return [
    {
      lat: CesiumMath.toDegrees(startCarto.latitude), // start latitude in degrees
      lng: CesiumMath.toDegrees(startCarto.longitude), // start longitude in degrees
      height: startCarto.height, // start height in meters
    },
    {
      lat: CesiumMath.toDegrees(endCarto.latitude), // end latitude in degrees
      lng: CesiumMath.toDegrees(endCarto.longitude), // end longitude in degrees
      height: endCarto.height, // end height in meters
    },
  ];
};

const normalizeLabel = (s) => {
  // Convert input safely to string (fallback to "")
  const str = s != null ? String(s) : "";

  // Force lowercase
  const lower = str.toLowerCase();

  // Remove non-alphanumeric characters
  const cleaned = lower.replace(/[^a-z0-9]+/g, "");

  // Return result (empty string if nothing left)
  return cleaned;
};

const decideOppositeSafe = (endRight, endLeft, directionRef, onlyRightDir, isAuto) => {
  const byEnds = ((r, l) => {
    const sd = (a, b) => (!a || !b ? Number.POSITIVE_INFINITY : (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
    const sameCost = sd(r.first, l.first) + sd(r.last, l.last);
    const crossCost = sd(r.first, l.last) + sd(r.last, l.first);
    return crossCost < sameCost;
  })(endRight, endLeft);

  if (isAuto || onlyRightDir || typeof directionRef?.current !== "boolean") return byEnds;

  return !directionRef.current;
};

const ensureLaneletGeoJSON = (layer) => {
  if (!layer.lenelet_geojson || typeof layer.lenelet_geojson !== "object" || !Array.isArray(layer.lenelet_geojson.features)) {
    layer.lenelet_geojson = { type: "FeatureCollection", features: [] };
  }
  return layer.lenelet_geojson;
};

let laneletHighlightOverlayDataSource = null;

const highlightPolylineEntity = (viewer, entity, color, width = 6) => {
  if (!viewer || !entity?.polyline) return () => {};

  if (!laneletHighlightOverlayDataSource) {
    laneletHighlightOverlayDataSource = new CustomDataSource("lanelet_highlight_overlay");
    viewer.dataSources.add(laneletHighlightOverlayDataSource);
  }
  const ds = laneletHighlightOverlayDataSource;

  const time = viewer.clock?.currentTime;
  const posProp = entity.polyline.positions;
  let arr;
  try {
    arr = typeof posProp?.getValue === "function" ? posProp.getValue(time) : posProp;
  } catch {
    arr = null;
  }
  if (!Array.isArray(arr) || arr.length < 2) return () => {};

  const elevated = arr.map((p) => {
    const c = Cartographic.fromCartesian(p);
    const baseH = c.height || 0;

    return Cartesian3.fromRadians(c.longitude, c.latitude, baseH + 0.2);
  });

  const overlayEntity = ds.entities.add({
    polyline: {
      positions: elevated,
      width,
      clampToGround: false,
      material: new ColorMaterialProperty(color),
    },
  });

  return () => {
    try {
      ds.entities.remove(overlayEntity);
    } catch {
      /* ignore */
    }
  };
};

const highlightPair = (viewer, first, second) => {
  const restoreFns = [];
  // right = first = BLUE
  restoreFns.push(highlightPolylineEntity(viewer, first?.entity, Color.BLUE, 6));
  // left = second = PURPLE
  restoreFns.push(highlightPolylineEntity(viewer, second?.entity, Color.PURPLE, 6));
  return () => restoreFns.forEach((r) => r?.());
};

const getNextId = (targetLayer, start = 9000000, step = 2) => {
  const ds = targetLayer?.dataSource;
  const ids = new Set();

  const readProp = (ent, key) => {
    const p = ent?.properties?.[key];
    if (!p) return undefined;
    return typeof p.getValue === "function" ? p.getValue() : p;
  };

  const collectIdsFromGeoJSON = (gjson) => {
    if (!gjson?.features || !Array.isArray(gjson.features)) return;

    for (const f of gjson.features) {
      const n = Number(f?.properties?.id);
      if (Number.isFinite(n)) ids.add(n);
    }
  };

  for (const gjson of [targetLayer?.geojson, targetLayer?.fullGeojson, targetLayer?.hiddenGeojson, targetLayer?.original_geojson, targetLayer?.lenelet_geojson]) {
    collectIdsFromGeoJSON(gjson);
  }

  const ents = ds?.entities?.values ?? [];
  for (const ent of ents) {
    const n = Number(readProp(ent, "id"));
    if (Number.isFinite(n)) ids.add(n);
  }

  const stored = Number(targetLayer?.bboxMaxFeaturePropertiesId ?? targetLayer?.bboxMaxId);
  const lastId = Number(targetLayer?.geojson_last_id);

  let maxId = -Infinity;
  if (Number.isFinite(stored)) maxId = Math.max(maxId, stored);
  if (Number.isFinite(lastId)) maxId = Math.max(maxId, lastId);

  for (const id of ids) {
    if (id > maxId) maxId = id;
  }

  const sameParity = (a, b) => ((a % 2) + 2) % 2 === ((b % 2) + 2) % 2;

  let nextId = Number.isFinite(maxId) ? maxId + step : start;
  if (!sameParity(nextId, start)) nextId += 1;

  while (ids.has(nextId)) {
    nextId += step;
    if (!sameParity(nextId, start)) nextId += 1;
  }

  if (targetLayer) {
    targetLayer.geojson_last_id = nextId;
    targetLayer.bboxMaxId = nextId;
    targetLayer.bboxMaxFeaturePropertiesId = nextId;
  }

  return nextId;
};

function colorFromHex(hex, alpha = 1) {
  try {
    const c = Color.fromCssColorString(hex);
    return alpha < 1 ? c.withAlpha(alpha) : c;
  } catch {
    return alpha < 1 ? Color.HOTPINK.withAlpha(alpha) : Color.HOTPINK;
  }
}

function toLonLatList(coords) {
  if (!coords) return [];

  // GeoJSON LineString
  if (coords.type === "LineString" && Array.isArray(coords.coordinates)) {
    return coords.coordinates.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => (p.length >= 3 ? [Number(p[0]), Number(p[1]), Number(p[2])] : [Number(p[0]), Number(p[1])]));
  }

  // Array of Cesium.Cartesian3
  if (Array.isArray(coords) && coords.length && coords[0] && coords[0].x != null && coords[0].y != null && coords[0].z != null) {
    const out = [];
    for (const c of coords) {
      const carto = Cartographic.fromCartesian(c);
      out.push([CesiumMath.toDegrees(carto.longitude), CesiumMath.toDegrees(carto.latitude), carto.height]);
    }
    return out;
  }

  // Flat number array [lon,lat,...] or [lon,lat,h,...]
  if (Array.isArray(coords) && coords.every((v) => typeof v === "number")) {
    const out = [];
    if (coords.length % 3 === 0) {
      for (let i = 0; i < coords.length; i += 3) {
        out.push([Number(coords[i]), Number(coords[i + 1]), Number(coords[i + 2])]);
      }
      return out;
    }
    if (coords.length % 2 === 0) {
      for (let i = 0; i < coords.length; i += 2) {
        out.push([Number(coords[i]), Number(coords[i + 1])]);
      }
      return out;
    }
    return [];
  }

  // Array of [lon,lat,(h)]
  if (Array.isArray(coords) && Array.isArray(coords[0])) {
    return coords.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => (p.length >= 3 ? [Number(p[0]), Number(p[1]), Number(p[2])] : [Number(p[0]), Number(p[1])]));
  }

  if (Array.isArray(coords) && coords.length && typeof coords[0] === "object" && !Array.isArray(coords[0])) {
    const out = [];
    for (const p of coords) {
      if (!p) continue;
      const lon = p.lon ?? p.lng ?? p.longitude;
      const lat = p.lat ?? p.latitude;
      const h = p.height ?? p.alt ?? p.altitude;
      if (lon == null || lat == null) continue;
      if (h != null) out.push([Number(lon), Number(lat), Number(h)]);
      else out.push([Number(lon), Number(lat)]);
    }
    return out;
  }

  return [];
}

function applyPolylineStyle(ds, strokeColor, width, defaultStyle) {
  const entities = ds.entities.values;
  if (!entities || entities.length === 0) return;

  // only last added polyline
  const e = entities[entities.length - 1];
  if (!e || !e.polyline) return;

  e.polyline.width = width;

  if (typeof e.polyline.clampToGround !== "undefined") {
    e.polyline.clampToGround = false;
  }

  if (defaultStyle === "dotted" || defaultStyle === "dashed") {
    const dashLength = defaultStyle === "dotted" ? 4.0 : 16.0;
    e.polyline.material = new PolylineDashMaterialProperty({
      color: strokeColor,
      dashLength,
    });
  } else {
    e.polyline.material = strokeColor;
  }
}

function normalizeToCesiumPositions(coords) {
  if (Array.isArray(coords) && coords.length && coords[0] instanceof Cartesian3) {
    return coords;
  }
  if (coords && coords.type === "LineString" && Array.isArray(coords.coordinates)) {
    coords = coords.coordinates;
  }
  const outFlat = [];
  let hasHeights = false;
  const pushLonLatH = (lon, lat, h) => {
    const nLon = Number(lon);
    const nLat = Number(lat);
    const nH = h !== undefined ? Number(h) : undefined;
    outFlat.push(nLon, nLat);
    if (nH !== undefined && Number.isFinite(nH)) {
      outFlat.push(nH);
      hasHeights = true;
    }
  };
  if (Array.isArray(coords)) {
    const looksLikeFlatNumbers = coords.every((v) => typeof v === "number");
    if (looksLikeFlatNumbers) {
      hasHeights = coords.length % 3 === 0 && coords.length >= 3;
      return hasHeights ? Cartesian3.fromDegreesArrayHeights(coords) : Cartesian3.fromDegreesArray(coords);
    }
    for (const pt of coords) {
      if (Array.isArray(pt)) {
        if (pt.length >= 2) pushLonLatH(pt[0], pt[1], pt[2]);
      } else if (pt && typeof pt === "object") {
        const lon = pt.lon ?? pt.lng ?? pt.longitude ?? pt.x;
        const lat = pt.lat ?? pt.latitude ?? pt.y;
        const h = pt.height ?? pt.alt ?? pt.altitude ?? pt.z;
        pushLonLatH(lon, lat, h);
      }
    }
  } else if (coords && typeof coords === "object") {
    const lon = coords.lon ?? coords.lng ?? coords.longitude ?? coords.x;
    const lat = coords.lat ?? coords.latitude ?? coords.y;
    const h = coords.height ?? coords.alt ?? coords.altitude ?? coords.z;
    pushLonLatH(lon, lat, h);
  }
  return hasHeights ? Cartesian3.fromDegreesArrayHeights(outFlat) : Cartesian3.fromDegreesArray(outFlat);
}

export function useLaneletBuilder({
  viewerRef,
  createLaneletMode,
  activeLayer,
  autoSelect,
  autoSelectRef,
  mapObjectsRef,
  setMapObjects,
  basicGeojsonStyle,
  setCenterLineLastFeature,
  infoboxRef,
  userData,
  createLaneletModeRef,
}) {
  const directionRef = useRef(null);
  const onlyRightDirRef = useRef(false);
  const lengthErrorOverlayRef = useRef({
    dataSource: null,
    entities: [],
    clickHandler: null,
  });

  const autoRunStatsRef = useRef({ active: false, count: 0, lengthMeters: 0 });
  const autoSaveRef = useRef({ enabled: false, lastValues: null });
  const activeLayerRef = useRef(activeLayer);
  const lastPairRef = useRef(null);
  const nextRunAutoAdvanceRef = useRef(false);

  const checkerDelayRef = useRef(100);
  const eligibleDataSourcesRef = useRef([]);

  const clearLengthErrorOverlay = () => {
    const viewer = viewerRef.current;
    const store = lengthErrorOverlayRef.current;
    if (!viewer || !store) return;

    if (store.dataSource && store.entities && store.entities.length) {
      try {
        store.entities.forEach((e) => {
          try {
            store.dataSource.entities.remove(e);
          } catch {}
        });
      } catch {}
    }

    if (store.clickHandler && viewer.canvas) {
      try {
        viewer.canvas.removeEventListener("click", store.clickHandler);
      } catch {}
    }

    lengthErrorOverlayRef.current = {
      dataSource: store.dataSource,
      entities: [],
      clickHandler: null,
    };
  };

  const showLengthErrorOverlay = (firstEntity, secondEntity) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    clearLengthErrorOverlay();

    let ds = lengthErrorOverlayRef.current.dataSource;
    if (!ds) {
      ds = new CustomDataSource("lanelet_length_error");
      viewer.dataSources.add(ds);
      lengthErrorOverlayRef.current.dataSource = ds;
    }

    const time = viewer.clock?.currentTime;
    const makeElevatedPolyline = (entity) => {
      if (!entity?.polyline) return null;
      const posProp = entity.polyline.positions;
      const arr = typeof posProp?.getValue === "function" ? posProp.getValue(time) : posProp;
      if (!Array.isArray(arr) || arr.length < 2) return null;

      const elevated = arr.map((p) => {
        const c = Cartographic.fromCartesian(p);
        const baseH = c.height || 0;
        return Cartesian3.fromRadians(c.longitude, c.latitude, baseH + 2.0);
      });

      return ds.entities.add({
        polyline: {
          positions: elevated,
          width: 6,
          clampToGround: false,
          material: Color.ORANGE,
        },
      });
    };

    const e1 = makeElevatedPolyline(firstEntity);
    const e2 = makeElevatedPolyline(secondEntity);
    const entities = [];
    if (e1) entities.push(e1);
    if (e2) entities.push(e2);

    const handler = () => {
      clearLengthErrorOverlay();
    };

    if (viewer.canvas && entities.length > 0) {
      viewer.canvas.addEventListener("click", handler, { once: true });
      lengthErrorOverlayRef.current = {
        dataSource: ds,
        entities,
        clickHandler: handler,
      };
    }
  };

  const startAutoRunSessionIfNeeded = () => {
    if (!autoRunStatsRef.current.active && autoSaveRef.current.enabled && autoSelect) {
      autoRunStatsRef.current = { active: true, count: 0, lengthMeters: 0 };
    }
  };

  const addToAutoRunStats = (positions) => {
    if (!autoRunStatsRef.current.active) return;
    try {
      const viewer = viewerRef.current;
      const time = viewer?.clock?.currentTime;
      const arr = Array.isArray(positions) ? positions : typeof positions?.getValue === "function" ? positions.getValue(time) : [];
      if (Array.isArray(arr) && arr.length > 1) {
        let sum = 0;
        for (let i = 1; i < arr.length; i++) sum += Cartesian3.distance(arr[i - 1], arr[i]);
        autoRunStatsRef.current.lengthMeters += sum;
      }
      autoRunStatsRef.current.count += 1;
    } catch {}
  };

  const showAutoRunSummary = () => {
    if (!autoRunStatsRef.current.active) return;
    const { count, lengthMeters } = autoRunStatsRef.current;
    autoRunStatsRef.current.active = false;
    const msgLen = lengthMeters >= 1000 ? `${(lengthMeters / 1000).toFixed(2)} km` : `${Math.round(lengthMeters)} m`;
    showToast(`Lanelets created: ${count} • Total length: ${msgLen}`, Type.NOTIFICATION.INFO);
  };

  useEffect(() => {
    activeLayerRef.current = activeLayer;
  }, [activeLayer]);

  useEffect(() => {
    if (!createLaneletMode) {
      autoSaveRef.current.enabled = false;
      autoSaveRef.current.lastValues = null;
      onlyRightDirRef.current = false;
      directionRef.current = null;
      autoRunStatsRef.current = { active: false, count: 0, lengthMeters: 0 };
    }
  }, [createLaneletMode]);

  useEffect(() => {
    if (createLaneletMode) {
      showToast("Lanelet mode active", Type.NOTIFICATION.INFO);
    }
  }, [createLaneletMode]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!createLaneletMode || !viewer || !viewer.dataSources) {
      eligibleDataSourcesRef.current = [];
      return;
    }

    const dsCollection = viewer.dataSources;
    const list = [];

    for (let i = 0; i < dsCollection.length; i++) {
      const ds = dsCollection.get(i);
      try {
        const ln = ds?.layerName ?? ds?.name ?? "";
        const t = detectLayerType(ln);
        if (t === "virtual" || t === "marker" || t === "turning") {
          list.push(ds);
        }
      } catch {}
    }

    eligibleDataSourcesRef.current = list;
  }, [createLaneletMode, viewerRef]);

  const getActiveLayerObj = () => {
    const mo = mapObjectsRef.current || [];
    const layerId = activeLayerRef.current;
    return mo.find((l) => l.id === layerId);
  };

  const patchLayer = (layerId, patch) => {
    setMapObjects((prev) => (Array.isArray(prev) ? prev.map((l) => (l.id === layerId ? { ...l, ...patch } : l)) : prev));
    mapObjectsRef.current = (mapObjectsRef.current || []).map((l) => (l.id === layerId ? { ...l, ...patch } : l));
  };

  const ensureLaneletPolygonDataSource = () => {
    const viewer = viewerRef.current;
    if (!viewer) return null;

    const layer = getActiveLayerObj();
    if (!layer) return null;

    if (layer.laneletDataSource && !layer.laneletDataSource.isDestroyed?.()) {
      layer.laneletDataSource.show = !!layer.show_lanelet;
      return layer.laneletDataSource;
    }

    const name = `lanelet_polygon_${layer.id}`;
    const ds = new CustomDataSource(name);

    ds.show = !!layer.show_lanelet;

    viewer.dataSources.add(ds);

    patchLayer(layer.id, { laneletDataSource: ds });

    return ds;
  };

  const addLaneletPolygonForCurrentLayer = (ring, props) => {
    if (!Array.isArray(ring) || ring.length < 4) return;

    const ds = ensureLaneletPolygonDataSource();
    if (!ds) return;

    const flat = [];
    for (const p of ring) {
      if (!Array.isArray(p) || p.length < 2) continue;
      flat.push(Number(p[0]), Number(p[1]));
    }
    if (flat.length < 6) return;

    const hierarchy = Cartesian3.fromDegreesArray(flat);

    ds.entities.add({
      polygon: {
        hierarchy,
        material: Color.fromCssColorString("#0000002a").withAlpha(0.3),
        outlineColor: Color.fromCssColorString("#0000002a"),
      },
      properties: {
        lanelet_id: props?.id,
        left_line_id: props?.left_line_id,
        right_line_id: props?.right_line_id,
      },
    });
  };

  function addSnappedLaneletPolyline(ds, rawCoords, props, strokeColor, strokeWidth) {
    const SNAP_RADIUS_M = 0.4;
    const CHECK_RADIUS_M = 1.2;
    if (!ds) return null;

    let coordList = toLonLatList(rawCoords);

    if (coordList.length >= 2) {
      const lastCL = getLastCenterlineEntity(ds);
      const prevEnds = lastCL ? getFirstLastLonLat(lastCL) : null;
      const prevEnd = prevEnds?.last;

      const snapped = snapStartToPrevEnd(coordList, prevEnd, SNAP_RADIUS_M, ds, {
        checkRadiusMeters: CHECK_RADIUS_M,
        requireStartSnap: true,
      });

      if (snapped && snapped.__snapStop) {
        showToast(snapped.__snapReason || "Lanelet start could not snap to nearby lanelet endpoint.", Type.NOTIFICATION.ERROR);
        return null;
      }

      if (snapped && snapped.length >= 2) {
        rawCoords = { type: "LineString", coordinates: snapped };
      }
    }

    const positions = normalizeToCesiumPositions(rawCoords);

    return ds.entities.add({
      properties: props,
      polyline: {
        positions,
        width: strokeWidth,
        clampToGround: false,
        material: strokeColor,
      },
    });
  }

  function snapStartToPrevEnd(list, prevEnd, snapRadiusMeters = 0.4, targetDs, opts = {}) {
    // list must be valid
    if (!Array.isArray(list) || list.length < 1) return list;

    const hasPrevEnd = Array.isArray(prevEnd) && prevEnd.length >= 2;

    const checkRadiusMeters = Number.isFinite(opts?.checkRadiusMeters) ? Number(opts.checkRadiusMeters) : 1.2;
    const requireStartSnap = opts?.requireStartSnap === true;

    const viewer = viewerRef?.current;
    if (!viewer) {
      // Fallback when viewer is not ready
      if (hasPrevEnd && Array.isArray(list[0]) && list[0].length >= 2) {
        const zTarget = Array.isArray(prevEnd) && prevEnd.length > 2 && Number.isFinite(prevEnd[2]) ? Number(prevEnd[2]) : list[0].length > 2 ? list[0][2] : 0;
        list[0] = [Number(prevEnd[0]), Number(prevEnd[1]), zTarget];
      }

      const endIdx = Math.max(0, list.length - 1);
      if (hasPrevEnd && endIdx > 0 && Array.isArray(list[endIdx]) && list[endIdx].length >= 2) {
        const zTarget = Array.isArray(prevEnd) && prevEnd.length > 2 && Number.isFinite(prevEnd[2]) ? Number(prevEnd[2]) : list[endIdx].length > 2 ? list[endIdx][2] : 0;
        list[endIdx] = [Number(prevEnd[0]), Number(prevEnd[1]), zTarget];
      }

      return list;
    }

    const time = viewer.clock?.currentTime;
    const sourceDs = targetDs || getActiveLayerObj()?.dataSource;

    if (!sourceDs?.entities?.values) {
      // No dataSource to snap against
      return list;
    }

    const TILE_DEG = 0.0005; // ~50 m

    const cartToLonLatLocal = (cart) => {
      if (!cart) return null;
      const carto = Cartographic.fromCartesian(cart);
      return {
        lon: CesiumMath.toDegrees(carto.longitude),
        lat: CesiumMath.toDegrees(carto.latitude),
        height: Number(carto.height) || 0,
      };
    };

    const buildSnapIndex = (ds) => {
      const index = new Map();
      const ents = ds?.entities?.values || [];

      const readProp = (obj) => {
        if (obj == null) return obj;
        if (typeof obj.getValue === "function") return obj.getValue(time);
        return obj;
      };

      for (let i = 0; i < ents.length; i++) {
        const ent = ents[i];
        if (!ent?.polyline) continue;

        const typeRaw = readProp(ent?.properties?.type) ?? readProp(ent?.properties?.tag) ?? readProp(ent?.properties?.layerType);
        if (typeRaw != null && String(typeRaw).trim() !== "") {
          const t = String(typeRaw).trim().toLowerCase();
          if (t !== "centerline") continue;
        }

        const seg = getFirstLastCartesian(ent, time);
        if (!seg) continue;

        const first = cartToLonLatLocal(seg.first);
        const last = cartToLonLatLocal(seg.last);

        const addPoint = (pt) => {
          if (!pt) return;
          const ix = Math.floor(pt.lon / TILE_DEG);
          const iy = Math.floor(pt.lat / TILE_DEG);
          const key = `${ix}:${iy}`;
          let bucket = index.get(key);
          if (!bucket) {
            bucket = [];
            index.set(key, bucket);
          }
          bucket.push({ lon: pt.lon, lat: pt.lat, height: Number(pt.height) || 0 });
        };

        addPoint(first);
        addPoint(last);
      }

      return {
        index,
        tileDeg: TILE_DEG,
      };
    };

    const getSnapIndex = (ds) => {
      const cacheKey = "__laneletSnapIndex";
      const ents = ds?.entities?.values || [];
      const entityCount = ents.length;

      const cache = buildSnapIndex(ds);
      cache.entityCount = entityCount;
      cache.tileDeg = TILE_DEG;
      ds[cacheKey] = cache;
      return cache;
    };

    const snapIndex = getSnapIndex(sourceDs);
    if (!snapIndex || !snapIndex.index) return list;

    const lonLatToCartesian = (lon, lat) => Cartesian3.fromDegrees(Number(lon), Number(lat), 0);

    const horizontalDist = (a, b) => {
      const ca = lonLatToCartesian(a[0], a[1]);
      const cb = lonLatToCartesian(b[0], b[1]);
      return Cartesian3.distance(ca, cb);
    };

    const getNearbyCandidates = (baseLon, baseLat, radiusMetersLocal) => {
      const { index, tileDeg } = snapIndex;
      const ix0 = Math.floor(baseLon / tileDeg);
      const iy0 = Math.floor(baseLat / tileDeg);

      const approxMetersPerDeg = 111000; // safe upper bound
      const maxRadiusDeg = radiusMetersLocal > 0 ? radiusMetersLocal / approxMetersPerDeg : tileDeg;
      const tilesRange = Math.max(1, Math.ceil(maxRadiusDeg / tileDeg));

      const result = [];

      for (let dx = -tilesRange; dx <= tilesRange; dx++) {
        for (let dy = -tilesRange; dy <= tilesRange; dy++) {
          const key = `${ix0 + dx}:${iy0 + dy}`;
          const bucket = index.get(key);
          if (!bucket || bucket.length === 0) continue;
          for (let i = 0; i < bucket.length; i++) {
            result.push(bucket[i]);
          }
        }
      }

      return result;
    };

    const trySnapIndexLocal = (idx) => {
      const pt = list[idx];
      if (!Array.isArray(pt) || pt.length < 2) return false;

      const beforeLon = Number(pt[0]);
      const beforeLat = Number(pt[1]);
      const beforeH = pt.length > 2 ? Number(pt[2]) : undefined;

      const base = [Number(pt[0]), Number(pt[1])];
      const baseLon = base[0];
      const baseLat = base[1];

      const nearby = getNearbyCandidates(baseLon, baseLat, snapRadiusMeters);

      let best = null;
      let bestDist = Infinity;

      for (let i = 0; i < nearby.length; i++) {
        const cand = nearby[i];
        const d = horizontalDist(base, [cand.lon, cand.lat]);
        if (d <= snapRadiusMeters && d < bestDist) {
          bestDist = d;
          best = cand;
        }
      }

      if (hasPrevEnd) {
        const prevBase = [Number(prevEnd[0]), Number(prevEnd[1])];
        const dPrev = horizontalDist(base, prevBase);
        if (dPrev <= snapRadiusMeters && dPrev < bestDist) {
          bestDist = dPrev;
          best = {
            lon: prevBase[0],
            lat: prevBase[1],
            height: prevEnd.length > 2 && Number.isFinite(prevEnd[2]) ? Number(prevEnd[2]) : undefined,
          };
        }
      }

      if (best) {
        const zTarget = Number.isFinite(best.height) ? Number(best.height) : pt.length > 2 ? pt[2] : 0;

        list[idx] = [best.lon, best.lat, zTarget];

        const after = list[idx];
        const moved = Number(after[0]) !== beforeLon || Number(after[1]) !== beforeLat || (Number.isFinite(beforeH) && Number(after[2]) !== beforeH);

        return moved || bestDist <= snapRadiusMeters;
      }

      return false;
    };

    // Always try to snap first and last point of the new lanelet
    const startSnapped = trySnapIndexLocal(0);
    const endIdx = Math.max(0, list.length - 1);
    if (endIdx > 0) trySnapIndexLocal(endIdx);

    // If start did not snap but there is a nearby endpoint within check radius, stop and warn
    if (requireStartSnap && !startSnapped) {
      const pt0 = list[0];
      if (Array.isArray(pt0) && pt0.length >= 2) {
        const base = [Number(pt0[0]), Number(pt0[1])];

        let hasNearby = false;

        // Check nearby endpoints in index (larger radius than snap)
        const nearby = getNearbyCandidates(base[0], base[1], checkRadiusMeters);
        for (let i = 0; i < nearby.length; i++) {
          const cand = nearby[i];
          const d = horizontalDist(base, [cand.lon, cand.lat]);
          if (d <= checkRadiusMeters) {
            hasNearby = true;
            break;
          }
        }

        // Also consider prevEnd as a nearby candidate
        if (!hasNearby && hasPrevEnd) {
          const prevBase = [Number(prevEnd[0]), Number(prevEnd[1])];
          const dPrev = horizontalDist(base, prevBase);
          if (dPrev <= checkRadiusMeters) hasNearby = true;
        }

        if (hasNearby) {
          list.__snapStop = true;
          list.__snapReason = `Lanelet start is within ${checkRadiusMeters}m of another endpoint but could not snap (snap radius ${snapRadiusMeters}m).`;
          return list;
        }
      }
    }
    return list;
  }

  function getFirstLastLonLat(entity) {
    const viewer = viewerRef.current;
    const time = viewer?.clock?.currentTime;

    const seg = getFirstLastCartesian(entity, time);
    if (!seg) return null;

    const cFirst = Cartographic.fromCartesian(seg.first);
    const cLast = Cartographic.fromCartesian(seg.last);

    return {
      first: [CesiumMath.toDegrees(cFirst.longitude), CesiumMath.toDegrees(cFirst.latitude), Number(cFirst.height) || 0],
      last: [CesiumMath.toDegrees(cLast.longitude), CesiumMath.toDegrees(cLast.latitude), Number(cLast.height) || 0],
    };
  }

  function getGeojsonOptionForFile(fileName) {
    let option = {};
    for (const key in basicGeojsonStyle) {
      if (fileName.toLowerCase().includes(key.toLowerCase())) {
        option = basicGeojsonStyle[key];
      }
    }
    return option;
  }

  function computeAutoNextPair(lastPair, isSameDirection, ignoreLeftDirection) {
    const epsilonMeters = 0.001;
    const viewer = viewerRef.current;
    const time = viewer?.clock?.currentTime;

    if (!viewer || !time) {
      directionRef.current = null;
      return { error: "Viewer or time is not available." };
    }

    if (!lastPair?.first?.entity || !lastPair?.second?.entity) {
      directionRef.current = null;
      return { error: "LastPair is missing one or both entities." };
    }

    // --- Basic geometry helpers ---

    function bearingRadians(p1, p2) {
      const c1 = Cartographic.fromCartesian(p1);
      const c2 = Cartographic.fromCartesian(p2);
      const dLon = c2.longitude - c1.longitude;
      const y = Math.sin(dLon) * Math.cos(c2.latitude);
      const x = Math.cos(c1.latitude) * Math.sin(c2.latitude) - Math.sin(c1.latitude) * Math.cos(c2.latitude) * Math.cos(dLon);
      let brng = Math.atan2(y, x);
      if (brng < 0) brng += CesiumMath.TWO_PI;
      return brng;
    }

    function smallestBearingDiff(a, b) {
      let d = Math.abs(b - a) % CesiumMath.TWO_PI;
      if (d > Math.PI) d = CesiumMath.TWO_PI - d;
      return d;
    }

    function horizontalDistanceMeters(a, b) {
      const ca = Cartographic.fromCartesian(a);
      const cb = Cartographic.fromCartesian(b);
      const pa = Cartesian3.fromRadians(ca.longitude, ca.latitude, 0);
      const pb = Cartesian3.fromRadians(cb.longitude, cb.latitude, 0);
      return Cartesian3.distance(pa, pb);
    }

    function isSamePoint(a, b, epsilon) {
      if (!a || !b) return false;
      const d = horizontalDistanceMeters(a, b);
      return Number.isFinite(d) && d <= epsilon;
    }

    function getPolylineCartesians(entity, t) {
      try {
        const posProp = entity?.polyline?.positions;
        if (!posProp) return [];
        const arr = posProp.getValue(t);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }

    function flyCameraToPair(nextFirst, nextSecond, viewerLocal, t) {
      if (!viewerLocal) return;
      const posFirst = getPolylineCartesians(nextFirst, t);
      const posSecond = getPolylineCartesians(nextSecond, t);

      const allPositions = [];
      if (Array.isArray(posFirst)) allPositions.push(...posFirst);
      if (Array.isArray(posSecond)) allPositions.push(...posSecond);
      if (!allPositions.length) return;

      let avgX = 0;
      let avgY = 0;
      let avgZ = 0;
      for (const p of allPositions) {
        avgX += p.x;
        avgY += p.y;
        avgZ += p.z;
      }
      const n = allPositions.length;
      const centerCartesian = new Cartesian3(avgX / n, avgY / n, avgZ / n);

      const currentCamera = viewerLocal.camera.positionCartographic;
      const height = currentCamera.height;
      const centerCarto = Cartographic.fromCartesian(centerCartesian);
      const destination = Cartesian3.fromRadians(centerCarto.longitude, centerCarto.latitude, height);

      viewerLocal.camera.setView({
        destination,
        orientation: {
          heading: viewerLocal.camera.heading,
          pitch: viewerLocal.camera.pitch,
          roll: viewerLocal.camera.roll,
        },
      });
    }

    function getPolylineLength(entity, t) {
      const positions = getPolylineCartesians(entity, t);
      if (!Array.isArray(positions) || positions.length < 2) return 0;

      let total = 0;
      for (let i = 1; i < positions.length; i++) {
        const p1 = positions[i - 1];
        const p2 = positions[i];

        const c1 = Cartographic.fromCartesian(p1);
        const c2 = Cartographic.fromCartesian(p2);

        const flat1 = Cartesian3.fromRadians(c1.longitude, c1.latitude, 0);
        const flat2 = Cartesian3.fromRadians(c2.longitude, c2.latitude, 0);

        total += Cartesian3.distance(flat1, flat2);
      }

      return total;
    }

    const BRANCH_TOL_DEG = 5;
    const ANG_TOL = CesiumMath.toRadians(BRANCH_TOL_DEG);

    function getEligibleDataSources() {
      return eligibleDataSourcesRef.current || [];
    }

    // --- Tile index over endpoints (lon/lat) ---

    const TILE_DEG = 0.0005; // ~50 m at mid-latitude

    // Build or reuse tile index attached to viewer
    function getOrBuildTileIndex() {
      const cacheKey = "__laneletTileIndex";
      const eligible = getEligibleDataSources();
      const dsList = eligible || [];

      let totalCount = 0;
      for (const ds of dsList) {
        const values = ds?.entities?.values || [];
        totalCount += values.length;
      }

      let cache = viewer[cacheKey];
      if (cache && cache.totalCount === totalCount && cache.tileDeg === TILE_DEG) {
        return cache;
      }

      const index = new Map();

      for (const ds of dsList) {
        const values = ds?.entities?.values || [];
        for (const entity of values) {
          if (!entity?.polyline) continue;
          const positions = getPolylineCartesians(entity, time);
          if (!Array.isArray(positions) || positions.length < 2) continue;

          const first = positions[0];
          const last = positions[positions.length - 1];

          const cFirst = Cartographic.fromCartesian(first);
          const cLast = Cartographic.fromCartesian(last);

          const lon1 = CesiumMath.toDegrees(cFirst.longitude);
          const lat1 = CesiumMath.toDegrees(cFirst.latitude);
          const lon2 = CesiumMath.toDegrees(cLast.longitude);
          const lat2 = CesiumMath.toDegrees(cLast.latitude);

          const ix1 = Math.floor(lon1 / TILE_DEG);
          const iy1 = Math.floor(lat1 / TILE_DEG);
          const key1 = `${ix1}:${iy1}`;

          const item = { entity, ds };

          let bucket1 = index.get(key1);
          if (!bucket1) {
            bucket1 = [];
            index.set(key1, bucket1);
          }
          bucket1.push(item);

          const ix2 = Math.floor(lon2 / TILE_DEG);
          const iy2 = Math.floor(lat2 / TILE_DEG);
          const key2 = `${ix2}:${iy2}`;

          if (key2 !== key1) {
            let bucket2 = index.get(key2);
            if (!bucket2) {
              bucket2 = [];
              index.set(key2, bucket2);
            }
            bucket2.push(item);
          }
        }
      }

      cache = { index, tileDeg: TILE_DEG, totalCount };
      viewer[cacheKey] = cache;
      return cache;
    }

    // Get candidate entities around a Cartesian point
    function getCandidatesNearCartesian(cart, expandTiles = 1) {
      const cache = getOrBuildTileIndex();
      if (!cache) return [];

      const carto = Cartographic.fromCartesian(cart);
      const lon = CesiumMath.toDegrees(carto.longitude);
      const lat = CesiumMath.toDegrees(carto.latitude);

      const baseIx = Math.floor(lon / cache.tileDeg);
      const baseIy = Math.floor(lat / cache.tileDeg);

      const result = [];
      const seen = new Set();

      for (let dx = -expandTiles; dx <= expandTiles; dx++) {
        for (let dy = -expandTiles; dy <= expandTiles; dy++) {
          const key = `${baseIx + dx}:${baseIy + dy}`;
          const bucket = cache.index.get(key);
          if (!bucket) continue;
          for (const item of bucket) {
            if (seen.has(item.entity)) continue;
            seen.add(item.entity);
            result.push(item);
          }
        }
      }

      return result;
    }

    // --- Find next polyline chaining from current (endpoint-to-endpoint) ---

    function findChainedNext(current, t, checkReverse = false) {
      const currentPositions = getPolylineCartesians(current, t);
      if (currentPositions.length < 2) return { candidate: null };

      const prefDs = current?.entityCollection?.owner || null;

      function headTail(positions, fromEnd = true) {
        const n = positions.length;
        const MIN = 0.5;
        if (fromEnd) {
          const head = positions[n - 1];
          let tail = positions[n - 2] || positions[n - 1];
          for (let i = n - 2; i >= 0; i--) {
            if (Cartesian3.distance(head, positions[i]) >= MIN) {
              tail = positions[i];
              break;
            }
          }
          return [head, tail];
        } else {
          const head = positions[0];
          let tail = positions[1] || positions[0];
          for (let i = 1; i < n; i++) {
            if (Cartesian3.distance(head, positions[i]) >= MIN) {
              tail = positions[i];
              break;
            }
          }
          return [head, tail];
        }
      }

      const [curA, curB] = checkReverse ? headTail(currentPositions, false) : headTail(currentPositions, true);
      const curBearing = bearingRadians(curA, curB);

      const withinTol = [];
      const withinTolPreferred = [];

      let bestCandidate = null;
      let smallestAngle = Math.PI;
      let branchCount = 0;

      const firstOfCurrent = currentPositions[0];
      const lastOfCurrent = currentPositions[currentPositions.length - 1];
      const junction = checkReverse ? firstOfCurrent : lastOfCurrent;

      const candidateItems = getCandidatesNearCartesian(junction, 1);

      for (const item of candidateItems) {
        const ds = item.ds;
        const candidate = item.entity;
        if (!candidate?.polyline || candidate === current) continue;

        const candPositions = getPolylineCartesians(candidate, t);
        if (candPositions.length < 2) continue;

        const firstOfCandidate = candPositions[0];
        const lastOfCandidate = candPositions[candPositions.length - 1];

        const connectOk = checkReverse ? isSamePoint(lastOfCandidate, firstOfCurrent, epsilonMeters) : isSamePoint(lastOfCurrent, firstOfCandidate, epsilonMeters);
        if (!connectOk) continue;

        branchCount += 1;

        const [candA, candB] = checkReverse ? headTail(candPositions, true) : headTail(candPositions, false);
        const candBearing = bearingRadians(candA, candB);

        const raw = smallestBearingDiff(curBearing, candBearing);
        const angle = Math.min(raw, Math.PI - raw);

        if (angle < smallestAngle) {
          smallestAngle = angle;
          bestCandidate = candidate;
        }

        if (angle <= ANG_TOL) {
          const itemInfo = { candidate, angle, ds, preferred: ds === prefDs };
          withinTol.push(itemInfo);
          if (itemInfo.preferred) withinTolPreferred.push(itemInfo);
        }
      }

      if (withinTolPreferred.length > 0) {
        withinTolPreferred.sort((a, b) => a.angle - b.angle);
        return { candidate: withinTolPreferred[0].candidate };
      }

      if (branchCount > 1 && withinTol.length > 1) {
        return {
          candidate: null,
          error: "Ambiguous junction: multiple outgoing branches within 5°. Select manually.",
        };
      }

      return { candidate: bestCandidate };
    }

    // --- Find next polyline from specific junction side (for left/ignore mode) ---

    function findChainedNextAt(current, t, junctionIsEnd) {
      const curPos = getPolylineCartesians(current, t);
      if (curPos.length < 2) return { candidate: null };

      const prefDs = current?.entityCollection?.owner || null;

      function pickHeadTail(positions, fromEnd) {
        const n = positions.length;
        const MIN = 0.5;
        if (fromEnd) {
          const head = positions[n - 1];
          let tail = positions[n - 2] || positions[n - 1];
          for (let i = n - 2; i >= 0; i--) {
            if (Cartesian3.distance(head, positions[i]) >= MIN) {
              tail = positions[i];
              break;
            }
          }
          return [head, tail];
        } else {
          const head = positions[0];
          let tail = positions[1] || positions[0];
          for (let i = 1; i < n; i++) {
            if (Cartesian3.distance(head, positions[i]) >= MIN) {
              tail = positions[i];
              break;
            }
          }
          return [head, tail];
        }
      }

      const [curA, curB] = pickHeadTail(curPos, junctionIsEnd);
      const curBearing = bearingRadians(curA, curB);

      let best = null;
      let smallest = Math.PI;
      let branchCount = 0;

      const withinTol = [];
      const withinTolPreferred = [];

      const junction = junctionIsEnd ? curPos[curPos.length - 1] : curPos[0];

      const candidateItems = getCandidatesNearCartesian(junction, 1);

      for (const item of candidateItems) {
        const ds = item.ds;
        const cand = item.entity;
        if (!cand?.polyline || cand === current) continue;

        const candPos = getPolylineCartesians(cand, t);
        if (candPos.length < 2) continue;

        const firstCand = candPos[0];
        const lastCand = candPos[candPos.length - 1];

        const connectAtStart = isSamePoint(firstCand, junction, epsilonMeters);
        const connectAtEnd = isSamePoint(lastCand, junction, epsilonMeters);
        if (!connectAtStart && !connectAtEnd) continue;

        branchCount += 1;

        const [candA, candB] = pickHeadTail(candPos, connectAtEnd);
        const candBearing = bearingRadians(candA, candB);

        const angRaw = smallestBearingDiff(curBearing, candBearing);
        const ang = Math.min(angRaw, Math.PI - angRaw);

        if (ang < smallest) {
          smallest = ang;
          best = cand;
        }

        if (ang <= ANG_TOL) {
          const itemInfo = { candidate: cand, angle: ang, ds, preferred: ds === prefDs };
          withinTol.push(itemInfo);
          if (itemInfo.preferred) withinTolPreferred.push(itemInfo);
        }
      }

      if (withinTolPreferred.length > 0) {
        withinTolPreferred.sort((a, b) => a.angle - b.angle);
        return { candidate: withinTolPreferred[0].candidate };
      }

      if (branchCount > 1 && withinTol.length > 1) {
        return {
          candidate: null,
          error: "Ambiguous junction: multiple branches within 5°.",
        };
      }

      return { candidate: best };
    }

    // --- Use the helpers for right/left pair ---

    const resFirst = findChainedNext(lastPair.first.entity, time, false);

    function currentPairSameDirection(pair) {
      const r = getFirstLastLonLat(pair.first.entity);
      const l = getFirstLastLonLat(pair.second.entity);
      if (!r || !l) return true;

      const sd = (a, b) => {
        if (!a || !b) return Infinity;
        const dx = (a[0] ?? 0) - (b[0] ?? 0);
        const dy = (a[1] ?? 0) - (b[1] ?? 0);
        return dx * dx + dy * dy;
      };

      const sameCost = sd(r.first, l.first) + sd(r.last, l.last);
      const crossCost = sd(r.first, l.last) + sd(r.last, l.first);
      return sameCost <= crossCost;
    }

    let resSecond;
    if (ignoreLeftDirection) {
      const samePair = currentPairSameDirection(lastPair);
      resSecond = findChainedNextAt(lastPair.second.entity, time, samePair);
    } else {
      resSecond = findChainedNext(lastPair.second.entity, time, isSameDirection === false);
    }

    if (resFirst?.error || resSecond?.error) {
      directionRef.current = null;
      return { error: resFirst?.error || resSecond?.error };
    }

    const nextFirst = resFirst.candidate;
    const nextSecond = resSecond.candidate;

    if (!nextFirst && !nextSecond) {
      directionRef.current = null;
      return {
        error: "No connected next polylines were found for both the RIGHT and LEFT lines.",
      };
    }
    if (!nextFirst) {
      directionRef.current = null;
      return { error: "No connected next polyline was found for the RIGHT line" };
    }
    if (!nextSecond) {
      directionRef.current = null;
      return { error: "No connected next polyline was found for the LEFT line." };
    }

    const lenFirst = getPolylineLength(nextFirst, time);
    const lenSecond = getPolylineLength(nextSecond, time);
    const maxLen = Math.max(lenFirst, lenSecond);
    const minLen = Math.min(lenFirst, lenSecond);

    if (maxLen > minLen * 1.3) {
      directionRef.current = null;
      return {
        error: "Length difference between next polylines exceeds 30%.",
        errorCode: "LENGTH_MISMATCH",
        firstEntity: nextFirst,
        secondEntity: nextSecond,
      };
    }

    let sameDirectionOut = false;
    const seg1 = getFirstLastCartesian(nextFirst, time);
    const seg2 = getFirstLastCartesian(nextSecond, time);
    if (seg1 && seg2) {
      const b1 = bearingRadians(seg1.first, seg1.last);
      const b2 = bearingRadians(seg2.first, seg2.last);
      const diff = smallestBearingDiff(b1, b2);
      const DIR_TOL_DEG = 5; // same tolerance as selectTwoPolylines
      sameDirectionOut = diff <= CesiumMath.toRadians(DIR_TOL_DEG);
    }

    flyCameraToPair(nextFirst, nextSecond, viewer, time);

    return {
      first: { ...lastPair.first, entity: nextFirst },
      second: { ...lastPair.second, entity: nextSecond },
      sameDirection: sameDirectionOut,
    };
  }

  function getCenterlineEntities(ds) {
    const viewer = viewerRef.current;
    const time = viewer?.clock?.currentTime;
    const readProp = (obj) => {
      if (obj == null) return obj;
      if (typeof obj.getValue === "function") return obj.getValue(time);
      return obj;
    };
    const values = ds?.entities?.values ?? [];
    return values.filter((ent) => {
      if (!ent?.polyline) return false;
      const type = readProp(ent?.properties?.type) ?? readProp(ent?.properties?.tag) ?? readProp(ent?.properties?.layerType);
      return type ? String(type).toLowerCase() === "centerline" : true;
    });
  }

  function getLastCenterlineEntity(ds) {
    const list = getCenterlineEntities(ds);
    return list.length ? list[list.length - 1] : null;
  }

  useEffect(() => {
    if (!createLaneletMode) return;

    let canceled = false;
    const cancelRef = {};

    const readProp = (obj) => {
      const viewer = viewerRef.current;
      const time = viewer?.clock?.currentTime;
      if (obj == null) return obj;
      if (typeof obj.getValue === "function") return obj.getValue(time);
      return obj;
    };

    const run = async () => {
      try {
        startAutoRunSessionIfNeeded();

        let first;
        let second;
        let sameDirection;
        let wasManualSelection = false;

        if (nextRunAutoAdvanceRef.current && lastPairRef.current) {
          nextRunAutoAdvanceRef.current = false;
          const autoPair = computeAutoNextPair(lastPairRef.current, directionRef.current, !!onlyRightDirRef.current);

          if (!autoPair?.error) {
            first = autoPair.first;
            second = autoPair.second;
            sameDirection = autoPair.sameDirection;
            wasManualSelection = false;
          } else {
            if (autoPair.errorCode === "LENGTH_MISMATCH" && autoPair.firstEntity && autoPair.secondEntity) {
              showLengthErrorOverlay(autoPair.firstEntity, autoPair.secondEntity);
            }

            showAutoRunSummary();
            showToast(autoPair?.error, Type.NOTIFICATION.WARNING, {
              duration: 12000,
              closable: true,
            });

            const picked = await selectTwoPolylines(viewerRef.current, cancelRef);
            if (canceled) {
              showAutoRunSummary();
              return;
            }
            [first, second, sameDirection] = picked;
            wasManualSelection = true;
          }
        } else {
          const picked = await selectTwoPolylines(viewerRef.current, cancelRef);
          if (canceled) {
            showAutoRunSummary();
            return;
          }
          [first, second, sameDirection] = picked;
          wasManualSelection = true;
        }

        directionRef.current = sameDirection;
        lastPairRef.current = { first, second };

        if (!wasManualSelection) {
          const prevRestore = cancelRef.restoreHighlights;
          const restoreOur = highlightPair(viewerRef.current, first, second);
          cancelRef.restoreHighlights = () => {
            try {
              restoreOur?.();
            } catch {}
            try {
              prevRestore?.();
            } catch {}
          };
        }

        // --- Auto rule: if both selected lines are dashed + yellow, force suicide lane settings
        const readEntProp = (ent, key) => {
          try {
            const p = ent?.properties?.[key];
            if (!p) return null;
            return typeof p.getValue === "function" ? p.getValue(viewerRef.current?.clock?.currentTime) : p;
          } catch {
            return null;
          }
        };

        const isYellowDashed = (ent) => {
          const subtype = readEntProp(ent, "subtype");
          const color = readEntProp(ent, "color");
          return String(subtype || "").toLowerCase() === "dashed" && String(color || "").toLowerCase() === "yellow";
        };

        const shouldApplySuicideRule = isYellowDashed(first.entity) && isYellowDashed(second.entity);

        const suicideOverrides = shouldApplySuicideRule ? { one_way: "no", function: "suicide lane", only_right_dir: "yes" } : null;

        // make sure the checkbox state (ref) also becomes enabled
        if (shouldApplySuicideRule) {
          onlyRightDirRef.current = true;
        }

        const coords = createCenterLineBetweenEntities(first.entity, second.entity);
        if (!coords) {
          cancelRef.restoreHighlights?.();
          showAutoRunSummary();
          run();
          return;
        }

        const info = createLineLinkInfoFromEntities(first.entity, second.entity, "id");

        const layerObj = getActiveLayerObj();
        if (!layerObj?.dataSource) {
          showAutoRunSummary();
          return;
        }
        const ds = layerObj.dataSource;

        const pairKey = (l, r) => `${l}__${r}`;
        const viewer = viewerRef.current;
        const time = viewer?.clock?.currentTime;

        // Only one scan over centerline entities
        const centerlines = getCenterlineEntities(ds);

        // Build existingPairs from same list
        const existingPairs = new Set();
        for (let i = 0; i < centerlines.length; i++) {
          const ent = centerlines[i];
          const l = readProp(ent?.properties?.left_line_id);
          const r = readProp(ent?.properties?.right_line_id);
          if (l != null && r != null) {
            existingPairs.add(pairKey(l, r));
          }
        }

        const leftId = info?.left_line_id;
        const rightId = info?.right_line_id;
        const forward = pairKey(leftId, rightId);
        const reverse = pairKey(rightId, leftId);
        const isDuplicate = existingPairs.has(forward) || existingPairs.has(reverse);
        if (isDuplicate) {
          showToast("This centerline already exists and won't be added again", Type.NOTIFICATION.WARNING);
          cancelRef.restoreHighlights?.();
          run();
          return;
        }

        // Reuse same centerlines array for lastEntity
        const lastEntity = centerlines.length ? centerlines[centerlines.length - 1] : null;
        const defaultsFromLast = lastEntity
          ? {
              direction: readProp(lastEntity?.properties?.turn_direction),
              participant: readProp(lastEntity?.properties?.participant),
              speed: readProp(lastEntity?.properties?.speed_limit),
              subtype: readProp(lastEntity?.properties?.subtype),
              one_way: readProp(lastEntity?.properties?.one_way),
              surface: readProp(lastEntity?.properties?.surface),
              function: readProp(lastEntity?.properties?.function),
            }
          : {};

        setCenterLineLastFeature(null);
        infoboxRef.current?.close();

        if (autoSaveRef.current.enabled && autoSaveRef.current.lastValues) {
          const data = { ...autoSaveRef.current.lastValues };
          if (suicideOverrides) Object.assign(data, suicideOverrides);

          const props = { ...(info || {}) };
          for (const k in data) props[k] = data[k];
          props.id = getNextId(layerObj, 9000000, 2);
          const creator = getUsernameAndUserIdFormat(userData);
          if (creator) props.creator = creator;

          let workingCoords = coords;
          let coordList = toLonLatList(workingCoords);
          if (coordList.length >= 2) {
            const lastCL = getLastCenterlineEntity(ds);
            const prevEnds = lastCL ? getFirstLastLonLat(lastCL) : null;
            const prevEnd = prevEnds?.last;
            if (Array.isArray(prevEnd) && prevEnd.length >= 2) {
              coordList = snapStartToPrevEnd(coordList, prevEnd, 0.4, ds, { checkRadiusMeters: 1.2, requireStartSnap: true });
              if (coordList && coordList.__snapStop) {
                showToast(coordList.__snapReason || "Lanelet start could not snap to nearby lanelet endpoint.", Type.NOTIFICATION.ERROR);
                return;
              }

              if (coordList.length >= 2) {
                workingCoords = { type: "LineString", coordinates: coordList };
              }
            }
          }

          const geojsonOption = getGeojsonOptionForFile("lanelet");
          const outlineHex = geojsonOption.defaultOutlineColor || "#ff69b4";
          const strokeWidth = Number(geojsonOption.defaultLineWidth ?? 4);
          const defaultStyle = geojsonOption.defaultStyle || "solid";
          const strokeColor = colorFromHex(outlineHex, 1);

          addSnappedLaneletPolyline(ds, workingCoords, props, strokeColor, strokeWidth);

          {
            const temp = [...(mapObjectsRef.current || [])];
            for (const obj of temp) {
              if (obj.dataSource?.layerName === layerObj.dataSource?.layerName) {
                obj.is_edited = true;
                break;
              }
            }
            setMapObjects(temp);
            mapObjectsRef.current = temp;
          }

          applyPolylineStyle(ds, strokeColor, strokeWidth, defaultStyle);

          const endRight = getFirstLastLonLat(first.entity);
          const endLeft = getFirstLastLonLat(second.entity);

          const isOpposite = decideOppositeSafe(endRight, endLeft, directionRef, !!onlyRightDirRef.current, !!autoSaveRef.current.enabled);

          const leftUsedFirst = isOpposite ? endLeft.last : endLeft.first;
          const leftUsedLast = isOpposite ? endLeft.first : endLeft.last;

          const ring = [endRight.first, endRight.last, leftUsedLast, leftUsedFirst, endRight.first];
          addLaneletPolygonForCurrentLayer(ring, props);

          const layerNow = getActiveLayerObj();
          const layerId2 = layerNow?.id;
          const existingGeo = ensureLaneletGeoJSON(layerNow); // always same object
          if (!Array.isArray(existingGeo.features)) {
            existingGeo.features = [];
          }
          const quadFeature = {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [ring] },
            properties: {
              kind: "centerline_quad",
              left_line_id: props.left_line_id,
              right_line_id: props.right_line_id,
            },
          };
          // mutate in-place: append only the new quad
          existingGeo.features.push(quadFeature);

          patchLayer(layerId2, {
            dataSource: ds,
            lenelet_geojson: existingGeo,
            is_edited: true,
          });

          nextRunAutoAdvanceRef.current = !!autoSelectRef.current;
          cancelRef.restoreHighlights?.();

          run();
          return;
        }

        const remapSavedKeys = (p) => {
          if (!p) return p;
          if (p.speed != null) {
            p.speed_limit = p.speed;
            delete p.speed;
          }
          if (p.direction != null) {
            p.turn_direction = p.direction;
            delete p.direction;
          }
          return p;
        };

        const mergedDefaults = {
          ...defaultsFromLast,
          ...(autoSaveRef.current.lastValues || {}),
          right_line_type: info?.right_line_type,
          left_line_type: info?.left_line_type,
          auto_save: autoSaveRef.current.enabled ? "yes" : "no",
          only_right_dir: onlyRightDirRef.current ? "yes" : "no",
          ...(suicideOverrides || {}), // <= add this line (must be last so it overrides)
        };

        if (autoSelectRef?.current) {
          if (["split_right", "split_left", "right", "left", "merge_right", "merge_left"].includes(mergedDefaults.direction)) {
            mergedDefaults.direction = "straight";
            mergedDefaults.speed = "45mph";
          }
        }
        showMapPopupForm({
          defaultValues: mergedDefaults,
          isSameDiration: directionRef.current,
          isSameDirection: directionRef.current,
          onSave: async (data) => {
            if (shouldApplySuicideRule) {
              data.one_way = "no";
              data.function = "suicide lane";
              data.only_right_dir = "yes";
            }

            const enableAuto = data.auto_save === "yes" || data.auto_save === true;
            delete data.auto_save;

            const onlyRight = data.only_right_dir === "yes" || data.only_right_dir === true;
            onlyRightDirRef.current = !!onlyRight;
            delete data.only_right_dir;

            autoSaveRef.current.lastValues = { ...data };
            autoSaveRef.current.enabled = !!enableAuto;
            if (autoSaveRef.current.enabled) autoSaveRef.current.lastValues = { ...data };

            const props = { ...(info || {}) };
            for (const k in data) props[k] = data[k];

            remapSavedKeys(props);

            props.id = getNextId(layerObj, 9000000, 2);
            const creator = getUsernameAndUserIdFormat(userData);
            if (creator) props.creator = creator;

            let workingCoords = coords;
            let coordList = toLonLatList(workingCoords);

            if (coordList.length >= 2) {
              const lastCL = getLastCenterlineEntity(ds);
              const prevEnds = lastCL ? getFirstLastLonLat(lastCL) : null;
              const prevEnd = prevEnds?.last;

              if (Array.isArray(prevEnd) && prevEnd.length >= 2) {
                coordList = snapStartToPrevEnd(coordList, prevEnd, 0.4, ds, { checkRadiusMeters: 1.2, requireStartSnap: true });
                if (coordList && coordList.__snapStop) {
                  showToast(coordList.__snapReason || "Lanelet start could not snap to nearby lanelet endpoint.", Type.NOTIFICATION.ERROR);
                  return;
                }

                if (coordList.length >= 2) {
                  workingCoords = { type: "LineString", coordinates: coordList };
                }
              }
            }

            const geojsonOption = getGeojsonOptionForFile("lanelet");
            const outlineHex = geojsonOption.defaultOutlineColor || "#ff69b4";
            const strokeWidth = Number(geojsonOption.defaultLineWidth ?? 4);
            const defaultStyle = geojsonOption.defaultStyle || "solid";
            const strokeColor = colorFromHex(outlineHex, 1);

            addSnappedLaneletPolyline(ds, workingCoords, props, strokeColor, strokeWidth);

            {
              const temp = [...(mapObjectsRef.current || [])];
              for (const obj of temp) {
                if (obj.dataSource?.layerName === layerObj.dataSource?.layerName) {
                  obj.is_edited = true;
                  break;
                }
              }
              setMapObjects(temp);
              mapObjectsRef.current = temp;
            }

            applyPolylineStyle(ds, strokeColor, strokeWidth, defaultStyle);

            const endRight = getFirstLastLonLat(first.entity);
            const endLeft = getFirstLastLonLat(second.entity);

            const isOpposite = decideOppositeSafe(endRight, endLeft, directionRef, !!onlyRightDirRef.current, !!autoSaveRef.current.enabled);

            const leftUsedFirst = isOpposite ? endLeft.last : endLeft.first;
            const leftUsedLast = isOpposite ? endLeft.first : endLeft.last;

            const ring = [endRight.first, endRight.last, leftUsedLast, leftUsedFirst, endRight.first];
            addLaneletPolygonForCurrentLayer(ring, props);

            const quadFeature = {
              type: "Feature",
              geometry: { type: "Polygon", coordinates: [ring] },
              properties: {
                kind: "centerline_quad",
                left_line_id: props.left_line_id,
                right_line_id: props.right_line_id,
              },
            };

            const layerNow = getActiveLayerObj();
            const layerId = layerNow?.id;
            const existingGeo = ensureLaneletGeoJSON(layerNow); // always same object
            if (!Array.isArray(existingGeo.features)) {
              existingGeo.features = [];
            }

            // mutate in-place: append only the new quad
            existingGeo.features.push(quadFeature);

            patchLayer(layerId, {
              dataSource: ds,
              lenelet_geojson: existingGeo,
              is_edited: true,
            });

            nextRunAutoAdvanceRef.current = !!autoSelectRef.current;
            cancelRef.restoreHighlights?.();
            run();
          },
          onClose: () => {
            directionRef.current = null;
            cancelRef.restoreHighlights?.();
            if (createLaneletModeRef?.current) run();
          },
        });
      } catch {
        showAutoRunSummary();
      }
    };

    run();

    return () => {
      canceled = true;
      if (cancelRef.cancel) cancelRef.cancel();
    };
  }, [createLaneletMode]);

  const removeLastCenterline = async () => {
    const layerObj = getActiveLayerObj();
    if (!layerObj?.dataSource) return;

    const ds = layerObj.dataSource;
    const entities = ds.entities;
    if (!entities || !entities.values || entities.values.length === 0) return;

    const time = viewerRef?.current?.clock?.currentTime;

    const readProp = (ent, key) => {
      const p = ent?.properties?.[key];
      if (!p) return undefined;
      try {
        return typeof p.getValue === "function" ? p.getValue(time) : p;
      } catch {
        return typeof p.getValue === "function" ? p.getValue() : p;
      }
    };

    // find all centerlines
    const centerlines = entities.values.filter((ent) => {
      if (!ent?.polyline) return false;
      const type = readProp(ent, "type") ?? readProp(ent, "tag") ?? readProp(ent, "layerType");
      return type ? String(type).toLowerCase() === "centerline" : true;
    });
    if (centerlines.length === 0) return;

    const last = centerlines[centerlines.length - 1];
    const lastId = readProp(last, "id");

    // remove last centerline
    showToast("Removed last lanelet", Type.NOTIFICATION.INFO);
    entities.remove(last);
    patchLayer(layerObj.id, { dataSource: ds });

    // also remove its polygon from laneletDataSource
    const polyDs = layerObj.laneletDataSource;
    if (polyDs && !polyDs.isDestroyed?.()) {
      const polyEntities = polyDs.entities;
      const list = polyEntities?.values || [];
      for (const ent of list) {
        const laneletId = readProp(ent, "lanelet_id");
        if (lastId != null && String(laneletId) === String(lastId)) {
          polyEntities.remove(ent);
          break; // remove only one polygon per lanelet
        }
      }
    }

    const geo = ensureLaneletGeoJSON(layerObj);
    if (Array.isArray(geo.features) && geo.features.length > 0) {
      geo.features.pop();
      patchLayer(layerObj.id, { lenelet_geojson: geo });
    }
  };

  return {
    removeLastCenterline,
  };
}
